import React, { useState, useContext } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import InputGroup from 'react-bootstrap/InputGroup';
import Spinner from 'react-bootstrap/Spinner';
import { LockFill } from 'react-bootstrap-icons';
import { NotificationContext } from '../context/NotificationContext';
import { AppContext } from '../window';
import { fetchWithCsrf, updateCsrfMetaToken } from '../api/fetch';
import { useTranslation } from '../i18n';

interface SetPasswordModalProps {
    show: boolean;
    onClose: () => void;
    // 'signup': email-first signup with an auto-generated password.
    // 'reset': arrived via a reset-password e-mail link.
    variant?: 'signup' | 'reset';
}

const MIN_PASSWORD_LENGTH = 4;

const SetPasswordModal: React.FC<SetPasswordModalProps> = ({ show, onClose, variant = 'signup' }) => {
    const { t } = useTranslation();
    const { addNotification } = useContext(NotificationContext);
    const { user, setUser } = useContext(AppContext);
    const [password, setPassword] = useState('');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleClose = () => {
        if (isSubmitting) return;
        setPassword('');
        setErrorMessage(null);
        onClose();
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setErrorMessage(null);

        if (!password || password.length < MIN_PASSWORD_LENGTH) {
            setErrorMessage(t('auth.validation_password_length'));
            return;
        }

        setIsSubmitting(true);
        try {
            const body = new URLSearchParams();
            // For users with passwordSetByUser=0 the server skips the
            // currentPassword check; we still send an empty value to keep
            // the form payload shape consistent.
            body.append('ChangePasswordForm[currentPassword]', '');
            body.append('ChangePasswordForm[password]', password);
            body.append('ChangePasswordForm[passwordRepeat]', password);

            const response = await fetchWithCsrf('/user/credentials/change-password?json=1', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: body.toString(),
            });
            const data = await response.json().catch(() => ({} as any));

            if (response.ok && (data.success || data.success === undefined && response.status === 200)) {
                // Changing the password re-logs the user in server-side, which
                // regenerates the CSRF token; sync it or the next POST fails.
                updateCsrfMetaToken(data.csrfToken);
                addNotification({
                    level: 'success',
                    message: t('landing_d.password_updated'),
                });
                // Flip the flag locally so the modal stops re-appearing
                if (user && user.credentials) {
                    setUser({
                        ...user,
                        credentials: {
                            ...user.credentials,
                            passwordSetByUser: 1,
                        },
                    });
                }
                setPassword('');
                setIsSubmitting(false);
                onClose();
                return;
            }

            setErrorMessage(data.error || t('auth.error_occurred'));
        } catch (err) {
            console.error('Set password error:', err);
            setErrorMessage(t('auth.error_occurred'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered backdrop="static" keyboard={!isSubmitting}>
            <Modal.Header closeButton>
                <Modal.Title>{t(variant === 'reset' ? 'landing_d.reset_password_title' : 'landing_d.set_password_title')}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <p>{t(variant === 'reset' ? 'landing_d.reset_password_body' : 'landing_d.set_password_body')}</p>
                    {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
                    <InputGroup>
                        <InputGroup.Text>
                            <LockFill />
                        </InputGroup.Text>
                        <Form.Floating>
                            <Form.Control
                                type="password"
                                name="password"
                                placeholder={t('auth.password')}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoFocus
                                required
                                minLength={MIN_PASSWORD_LENGTH}
                            />
                            <Form.Label>{t('auth.password_required')}</Form.Label>
                        </Form.Floating>
                    </InputGroup>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="link" onClick={handleClose} disabled={isSubmitting}>
                        {t('landing_d.set_password_later')}
                    </Button>
                    <Button variant="success" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                            <span className="d-flex align-items-center gap-2">
                                <Spinner animation="border" size="sm" />
                                <span>{t('notification_settings.updating')}</span>
                            </span>
                        ) : t('landing_d.set_password_save')}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default SetPasswordModal;
